import { api } from './http';
import { definedFields } from './searchBody';
import type {
  EmbeddingHealth,
  EmbeddingStats,
  FullSearchResults,
  ReindexResult,
  SearchContactResult,
  SearchDiagnostics,
  SearchDocumentResult,
  SearchMatterResult,
  SearchResults,
  SearchSyncRun,
  SemanticResult,
  SimilarMatter,
} from './types';

// The searches below send what the user typed (usually a client's name) in a POST
// body: in a GET query string it would land in nginx access logs and Sentry
// breadcrumbs (#2028). The GET routes remain for older callers.

export const globalSearch = (q: string, semantic?: boolean) =>
  api.post<SearchResults>('/search', { q, ...(semantic ? { semantic: true } : {}) }).then(r => r.data);

export const fullSearch = (q: string, params?: { tab?: string; limit?: number; offset?: number; include_archived?: boolean }) =>
  api.post<FullSearchResults>('/search/full', definedFields({ q, ...params })).then(r => r.data);

interface InstantSearchResponse<T> {
  hits: T[];
  query: string;
  source: string;
}

export const instantGlobalSearch = async (q: string): Promise<SearchResults> => {
  const [matters, contacts, documents] = await Promise.all([
    api.post<InstantSearchResponse<SearchMatterResult>>('/instant-search/matters', { q }).then(r => r.data),
    api.post<InstantSearchResponse<SearchContactResult>>('/instant-search/contacts', { q }).then(r => r.data),
    api.post<InstantSearchResponse<SearchDocumentResult>>('/instant-search/documents', { q }).then(r => r.data),
  ]);
  return {
    matters: matters.hits,
    contacts: contacts.hits,
    documents: documents.hits,
    legal_references: [],
    semantic_results: [],
    semantic_enabled: false,
  };
};

export const getSearchDiagnostics = () =>
  api.get<SearchDiagnostics>('/instant-search/status').then(r => r.data);

export const triggerInstantSearchSync = () =>
  api.post<{ synced: Record<string, number> }>('/instant-search/sync').then(r => r.data);

export const getSearchSyncHistory = (limit = 10) =>
  api.get<SearchSyncRun[]>('/instant-search/history', { params: { limit } }).then(r => r.data);

export const getEmbeddingStats = () =>
  api.get<EmbeddingStats>('/embeddings/stats').then(r => r.data);

export const getEmbeddingHealth = () =>
  api.get<EmbeddingHealth>('/embeddings/health').then(r => r.data);

export const triggerReindex = (batchSize?: number) =>
  api.post<ReindexResult>('/embeddings/reindex', null, { params: batchSize ? { batch_size: batchSize } : {} }).then(r => r.data);

export const findSimilarContent = (contentType: string, sourceId: number, limit?: number, sameTypeOnly?: boolean) =>
  api.get<{ results: SemanticResult[] }>('/embeddings/similar', { params: { content_type: contentType, source_id: sourceId, ...(limit ? { limit } : {}), ...(sameTypeOnly ? { same_type_only: true } : {}) } }).then(r => r.data);

export const getSimilarMatters = (matterId: number, limit: number = 5) =>
  api.get<SimilarMatter[]>('/embeddings/similar-matters', {
    params: { matter_id: matterId, limit },
  }).then(r => r.data);
